import React, { useState } from 'react'
import './GoalPlanner.css'

interface Goal {
  id: number
  title: string
  description: string
  category: string
  priority: 'high' | 'medium' | 'low'
  status: 'planning' | 'in-progress' | 'completed' | 'paused'
  progress: number
  startDate: string
  dueDate: string
  milestones: Milestone[]
  tags: string[]
}

interface Milestone {
  id: number
  title: string
  completed: boolean
  date: string
}

const GoalPlanner: React.FC = () => {
  const [goals, setGoals] = useState<Goal[]>([
    {
      id: 1,
      title: '一起旅行到日本',
      description: '计划在樱花季节去日本旅行，体验当地文化，品尝美食，留下美好回忆。',
      category: '旅行',
      priority: 'high',
      status: 'in-progress',
      progress: 75,
      startDate: '2024-01-01',
      dueDate: '2024-04-01',
      milestones: [
        { id: 1, title: '办理护照', completed: true, date: '2024-01-15' },
        { id: 2, title: '预订机票', completed: true, date: '2024-02-01' },
        { id: 3, title: '预订酒店', completed: true, date: '2024-02-15' },
        { id: 4, title: '制定行程计划', completed: false, date: '2024-03-01' }
      ],
      tags: ['旅行', '日本', '樱花']
    },
    {
      id: 2,
      title: '学习一门新语言',
      description: '一起学习法语，为将来的法国之旅做准备。每周至少学习3次，每次1小时。',
      category: '学习',
      priority: 'medium',
      status: 'in-progress',
      progress: 45,
      startDate: '2024-01-10',
      dueDate: '2024-06-10',
      milestones: [
        { id: 5, title: '完成基础语法学习', completed: true, date: '2024-02-10' },
        { id: 6, title: '掌握500个常用词汇', completed: false, date: '2024-03-10' },
        { id: 7, title: '能进行简单对话', completed: false, date: '2024-04-10' }
      ],
      tags: ['学习', '法语', '语言']
    },
    {
      id: 3,
      title: '健身计划',
      description: '制定情侣健身计划，每周一起运动3次，保持健康的生活方式。',
      category: '健康',
      priority: 'medium',
      status: 'in-progress',
      progress: 60,
      startDate: '2024-01-05',
      dueDate: '2024-12-31',
      milestones: [
        { id: 8, title: '办理健身房会员', completed: true, date: '2024-01-05' },
        { id: 9, title: '制定训练计划', completed: true, date: '2024-01-10' },
        { id: 10, title: '坚持3个月', completed: false, date: '2024-04-05' }
      ],
      tags: ['健身', '健康', '运动']
    }
  ])

  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterCategory, setFilterCategory] = useState('')

  const handleAddGoal = (formData: FormData) => {
    const newGoal: Goal = {
      id: Math.max(...goals.map(g => g.id)) + 1,
      title: formData.get('title') as string,
      description: formData.get('description') as string,
      category: formData.get('category') as string,
      priority: formData.get('priority') as 'high' | 'medium' | 'low',
      status: formData.get('status') as 'planning' | 'in-progress' | 'completed' | 'paused',
      progress: 0,
      startDate: formData.get('startDate') as string,
      dueDate: formData.get('dueDate') as string,
      milestones: [],
      tags: (formData.get('tags') as string).split(',').map(tag => tag.trim()).filter(tag => tag)
    }
    
    setGoals(prev => [...prev, newGoal])
    setShowAddForm(false)
  }

  const handleEditGoal = (goalId: number) => {
    const goalToEdit = goals.find(goal => goal.id === goalId)
    if (goalToEdit) {
      setEditingGoal(goalToEdit)
      setShowEditForm(true)
    }
  }

  const handleUpdateGoal = (formData: FormData) => {
    if (!editingGoal) return
    
    const updatedGoal = {
      ...editingGoal,
      title: formData.get('title') as string,
      description: formData.get('description') as string,
      category: formData.get('category') as string,
      priority: formData.get('priority') as 'high' | 'medium' | 'low',
      status: formData.get('status') as 'planning' | 'in-progress' | 'completed' | 'paused',
      startDate: formData.get('startDate') as string,
      dueDate: formData.get('dueDate') as string,
      tags: (formData.get('tags') as string).split(',').map(tag => tag.trim()).filter(tag => tag)
    }
    
    setGoals(prev => prev.map(goal => 
      goal.id === editingGoal.id ? updatedGoal : goal
    ))
    
    setShowEditForm(false)
    setEditingGoal(null)
  }

  const handleDeleteGoal = (goalId: number) => {
    if (confirm('确定要删除这个目标吗？')) {
      setGoals(prev => prev.filter(goal => goal.id !== goalId))
    }
  }

  const categories = ['旅行', '学习', '健康', '工作', '兴趣', '其他']
  const statuses = [
    { value: 'planning', label: '计划中', color: '#ffa500' },
    { value: 'in-progress', label: '进行中', color: '#4169e1' },
    { value: 'completed', label: '已完成', color: '#32cd32' },
    { value: 'paused', label: '暂停', color: '#ff6b6b' }
  ]

  const priorities = [
    { value: 'high', label: '高', color: '#ff4757' },
    { value: 'medium', label: '中', color: '#ffa502' },
    { value: 'low', label: '低', color: '#2ed573' }
  ]

  const filteredGoals = goals.filter(goal => {
    const matchesStatus = !filterStatus || goal.status === filterStatus
    const matchesCategory = !filterCategory || goal.category === filterCategory
    return matchesStatus && matchesCategory
  })

  // const updateGoalProgress = (goalId: number, progress: number) => {
  //   setGoals(prev => prev.map(goal => 
  //     goal.id === goalId ? { ...goal, progress } : goal
  //   ))
  // }

  const toggleMilestone = (goalId: number, milestoneId: number) => {
    setGoals(prev => prev.map(goal => {
      if (goal.id === goalId) {
        const updatedMilestones = goal.milestones.map(milestone => 
          milestone.id === milestoneId 
            ? { ...milestone, completed: !milestone.completed }
            : milestone
        )
        const completedCount = updatedMilestones.filter(m => m.completed).length
        const newProgress = Math.round((completedCount / updatedMilestones.length) * 100)
        
        return {
          ...goal,
          milestones: updatedMilestones,
          progress: newProgress
        }
      }
      return goal
    }))
  }

  const getStatusColor = (status: string) => {
    return statuses.find(s => s.value === status)?.color || '#666'
  }

  const getPriorityColor = (priority: string) => {
    return priorities.find(p => p.value === priority)?.color || '#666'
  }

  return (
    <div className="goal-planner">
      <div className="planner-header">
        <h2 className="planner-title">目标规划 🎯</h2>
        <button 
          className="btn btn-primary add-goal-btn"
          onClick={() => setShowAddForm(true)}
        >
          ➕ 添加目标
        </button>
      </div>

      <div className="planner-filters">
        <div className="filter-group">
          <label>状态筛选：</label>
          <select 
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">全部状态</option>
            {statuses.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label>分类筛选：</label>
          <select 
            className="filter-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="">全部分类</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="goals-overview">
        <div className="overview-stats">
          <div className="stat-item">
            <div className="stat-number">{goals.length}</div>
            <div className="stat-label">总目标</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">{goals.filter(g => g.status === 'completed').length}</div>
            <div className="stat-label">已完成</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">{goals.filter(g => g.status === 'in-progress').length}</div>
            <div className="stat-label">进行中</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">
              {Math.round(goals.reduce((sum, goal) => sum + goal.progress, 0) / goals.length)}%
            </div>
            <div className="stat-label">平均进度</div>
          </div>
        </div>
      </div>

      <div className="goals-grid">
        {filteredGoals.map(goal => (
          <div key={goal.id} className="goal-card">
            <div className="goal-header">
              <div className="goal-title-section">
                <h3 className="goal-title">{goal.title}</h3>
                <div className="goal-badges">
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(goal.status) }}
                  >
                    {statuses.find(s => s.value === goal.status)?.label}
                  </span>
                  <span 
                    className="priority-badge"
                    style={{ backgroundColor: getPriorityColor(goal.priority) }}
                  >
                    {priorities.find(p => p.value === goal.priority)?.label}优先级
                  </span>
                </div>
              </div>
              <div className="goal-category">{goal.category}</div>
            </div>
            
            <p className="goal-description">{goal.description}</p>
            
            <div className="goal-progress-section">
              <div className="progress-header">
                <span>进度</span>
                <span>{goal.progress}%</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${goal.progress}%` }}
                ></div>
              </div>
            </div>
            
            <div className="goal-dates">
              <div className="date-item">
                <span className="date-label">开始：</span>
                <span className="date-value">{goal.startDate}</span>
              </div>
              <div className="date-item">
                <span className="date-label">截止：</span>
                <span className="date-value">{goal.dueDate}</span>
              </div>
            </div>
            
            <div className="goal-tags">
              {goal.tags.map(tag => (
                <span key={tag} className="tag">#{tag}</span>
              ))}
            </div>
            
            <div className="goal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setSelectedGoal(goal)}
              >
                查看详情
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => handleEditGoal(goal.id)}
              >
                编辑目标
              </button>
              <button 
                className="btn btn-danger"
                onClick={() => handleDeleteGoal(goal.id)}
                style={{ backgroundColor: '#ff6b6b', color: 'white' }}
              >
                删除
              </button>
            </div>
          </div>
        ))}
      </div>

      {selectedGoal && (
        <div className="modal-overlay" onClick={() => setSelectedGoal(null)}>
          <div className="goal-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedGoal.title}</h3>
              <button 
                className="close-btn"
                onClick={() => setSelectedGoal(null)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <div className="goal-detail-info">
                <p className="goal-detail-description">{selectedGoal.description}</p>
                
                <div className="detail-progress">
                  <div className="progress-header">
                    <span>整体进度</span>
                    <span>{selectedGoal.progress}%</span>
                  </div>
                  <div className="progress-bar large">
                    <div 
                      className="progress-fill"
                      style={{ width: `${selectedGoal.progress}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="milestones-section">
                  <h4>里程碑</h4>
                  <div className="milestones-list">
                    {selectedGoal.milestones.map(milestone => (
                      <div key={milestone.id} className="milestone-item">
                        <label className="milestone-checkbox">
                          <input
                            type="checkbox"
                            checked={milestone.completed}
                            onChange={() => toggleMilestone(selectedGoal.id, milestone.id)}
                          />
                          <span className="checkmark"></span>
                          <span className={`milestone-title ${milestone.completed ? 'completed' : ''}`}>
                            {milestone.title}
                          </span>
                        </label>
                        <span className="milestone-date">{milestone.date}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddForm && (
        <div className="modal-overlay" onClick={() => setShowAddForm(false)}>
          <div className="add-goal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>添加新目标</h3>
              <button 
                className="close-btn"
                onClick={() => setShowAddForm(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <form className="add-goal-form" onSubmit={(e) => {
                e.preventDefault()
                const formData = new FormData(e.currentTarget)
                handleAddGoal(formData)
              }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>目标标题</label>
                    <input type="text" name="title" className="input" placeholder="输入目标标题..." />
                  </div>
                  <div className="form-group">
                    <label>分类</label>
                    <select name="category" className="input">
                      <option value="">选择分类</option>
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>目标描述</label>
                  <textarea name="description" className="textarea" placeholder="详细描述这个目标..."></textarea>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>优先级</label>
                    <select name="priority" className="input">
                      <option value="">选择优先级</option>
                      {priorities.map(priority => (
                        <option key={priority.value} value={priority.value}>{priority.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>状态</label>
                    <select name="status" className="input">
                      {statuses.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>开始日期</label>
                    <input type="date" name="startDate" className="input" />
                  </div>
                  <div className="form-group">
                    <label>截止日期</label>
                    <input type="date" name="dueDate" className="input" />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>标签</label>
                  <input type="text" name="tags" className="input" placeholder="用逗号分隔多个标签" />
                </div>
                
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                    取消
                  </button>
                  <button type="submit" className="btn btn-primary">
                    创建目标
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showEditForm && editingGoal && (
        <div className="modal-overlay" onClick={() => setShowEditForm(false)}>
          <div className="add-goal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>编辑目标</h3>
              <button 
                className="close-btn"
                onClick={() => setShowEditForm(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <form className="add-goal-form" onSubmit={(e) => {
                e.preventDefault()
                const formData = new FormData(e.currentTarget)
                handleUpdateGoal(formData)
              }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>目标标题</label>
                    <input 
                      type="text" 
                      name="title" 
                      className="input" 
                      defaultValue={editingGoal.title}
                      placeholder="输入目标标题..." 
                    />
                  </div>
                  <div className="form-group">
                    <label>分类</label>
                    <select name="category" className="input" defaultValue={editingGoal.category}>
                      <option value="">选择分类</option>
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>目标描述</label>
                  <textarea 
                    name="description" 
                    className="textarea" 
                    defaultValue={editingGoal.description}
                    placeholder="详细描述这个目标..."
                  ></textarea>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>优先级</label>
                    <select name="priority" className="input" defaultValue={editingGoal.priority}>
                      <option value="">选择优先级</option>
                      {priorities.map(priority => (
                        <option key={priority.value} value={priority.value}>{priority.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>状态</label>
                    <select name="status" className="input" defaultValue={editingGoal.status}>
                      {statuses.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>开始日期</label>
                    <input 
                      type="date" 
                      name="startDate" 
                      className="input" 
                      defaultValue={editingGoal.startDate}
                    />
                  </div>
                  <div className="form-group">
                    <label>截止日期</label>
                    <input 
                      type="date" 
                      name="dueDate" 
                      className="input" 
                      defaultValue={editingGoal.dueDate}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>标签</label>
                  <input 
                    type="text" 
                    name="tags" 
                    className="input" 
                    defaultValue={editingGoal.tags.join(', ')}
                    placeholder="用逗号分隔多个标签" 
                  />
                </div>
                
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEditForm(false)}>
                    取消
                  </button>
                  <button type="submit" className="btn btn-primary">
                    保存更改
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GoalPlanner